import { Link } from "@tanstack/react-router";
import { ArrowRight, Phone, MessageCircle, Calendar, Clock } from "lucide-react";
import type { Guide } from "@/lib/guides";
import { FAQAccordion } from "@/components/FAQAccordion";
import { getBusinessConfig } from "@/lib/site-config";
import { BlogAuthorBlock } from "@/components/BlogAuthorBlock";
import { GuideEnquiryBanner } from "@/components/GuideEnquiryBanner";

interface Props {
  guide: Guide;
  related: Guide[];
}

export function GuidePageTemplate({ guide, related }: Props) {
  const cfg = getBusinessConfig();
  return (
    <>
      <section className="py-12 md:py-16 bg-secondary">
        <div className="mx-auto max-w-3xl px-4">
          <p className="text-[11px] font-bold tracking-[0.22em] uppercase text-primary">{guide.category}</p>
          <h1 className="mt-3 text-3xl md:text-5xl font-extrabold text-foreground tracking-tight leading-tight">
            {guide.title}
          </h1>
          <p className="mt-4 text-base md:text-lg text-muted-foreground leading-relaxed">{guide.excerpt}</p>
          <div className="mt-5 flex flex-wrap items-center gap-4 text-xs text-muted-foreground">
            <span className="inline-flex items-center gap-1.5"><Calendar className="h-3.5 w-3.5" />Updated {new Date(guide.publishedAt).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}</span>
            <span className="inline-flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" />{guide.readingMinutes} min read</span>
          </div>
        </div>
      </section>

      <article className="py-12 md:py-16">
        <div className="mx-auto max-w-3xl px-4 space-y-10">
          {guide.sections.map((s) => (
            <section key={s.heading}>
              <h2 className="text-xl md:text-2xl font-bold text-foreground">{s.heading}</h2>
              {s.body?.map((p, i) => (
                <p key={i} className="mt-3 text-[15px] leading-relaxed text-muted-foreground">{p}</p>
              ))}
              {s.bullets && (
                <ul className="mt-4 space-y-2 text-[15px] text-muted-foreground">
                  {s.bullets.map((b) => (
                    <li key={b} className="flex gap-2"><span className="text-primary mt-1.5 h-1.5 w-1.5 rounded-full bg-primary shrink-0" />{b}</li>
                  ))}
                </ul>
              )}
            </section>
          ))}

          <GuideEnquiryBanner
            guideSlug={guide.slug}
            service={guide.relatedServices[0]?.label}
          />



          <div className="rounded-2xl border border-border bg-card p-6">
            <h3 className="text-lg font-bold text-foreground">Need a hand from a real garage?</h3>
            <p className="mt-2 text-sm text-muted-foreground">
              We're an independent DVSA-authorised workshop in Poplar, E14 — five minutes from Canary Wharf. Honest quotes, no scare tactics.
            </p>
            <div className="mt-4 flex flex-wrap gap-2">
              <a href={cfg.phoneHref} className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2.5 text-sm font-bold text-primary-foreground">
                <Phone className="h-4 w-4" />Call {cfg.phone}
              </a>
              <a href={cfg.whatsappUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 rounded-lg border border-border bg-background px-4 py-2.5 text-sm font-bold text-foreground">
                <MessageCircle className="h-4 w-4" />WhatsApp
              </a>
              <Link to="/book" className="inline-flex items-center gap-2 rounded-lg border border-border bg-background px-4 py-2.5 text-sm font-bold text-foreground">
                Request booking<ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>

          {guide.relatedServices.length > 0 && (
            <section>
              <h3 className="text-base font-bold text-foreground">Related services</h3>
              <div className="mt-3 flex flex-wrap gap-2">
                {guide.relatedServices.map((s) => (
                  <Link key={s.to} to={s.to} className="rounded-lg border border-border bg-card px-3 py-2 text-sm font-semibold text-foreground hover:bg-secondary">
                    {s.label}
                  </Link>
                ))}
              </div>
            </section>
          )}

          <BlogAuthorBlock />
        </div>
      </article>

      <FAQAccordion faqs={guide.faqs} title="Frequently asked questions" />

      {related.length > 0 && (
        <section className="py-14 md:py-16">
          <div className="mx-auto max-w-3xl px-4">
            <h2 className="text-2xl font-bold text-foreground">Related guides</h2>
            <div className="mt-5 grid sm:grid-cols-2 gap-4">
              {related.map((g) => (
                <Link key={g.slug} to="/guide/$slug" params={{ slug: g.slug }} className="block rounded-2xl border border-border bg-card p-5 hover:bg-secondary">
                  <p className="text-[11px] font-bold tracking-[0.18em] uppercase text-primary">{g.category}</p>
                  <p className="mt-1.5 text-sm font-bold text-foreground leading-snug">{g.title}</p>
                  <p className="mt-2 text-xs text-muted-foreground line-clamp-2">{g.excerpt}</p>
                </Link>
              ))}
              <Link to="/motorist-help" className="block rounded-2xl border border-dashed border-border p-5 hover:bg-secondary">
                <p className="text-sm font-bold text-foreground">All motorist guides</p>
                <p className="mt-2 text-xs text-muted-foreground">See the full hub of free advice for East London drivers.</p>
              </Link>
            </div>
          </div>
        </section>
      )}
    </>
  );
}
